import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { productsData as staticProductsData } from "../data/productdata";
import "../css/productdetail.css";
import WhatsAppFloat from "../components/whatsapp";
import Footer from "../components/footer";

import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

const stripePromise = loadStripe(
  "pk_live_51TiiSs2M5uEs7QwcI8dnUkwXMRH7KI1YCuRitFaHQmN0DeLWhkex3Rn77MyyJcHTcssTtk2nhuwiUdN1VNtJ97v200g9FWaOw0",
);

const StripeCheckoutForm = ({ price, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage("");

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      onSuccess(paymentIntent.id);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: "1rem" }}>
      <PaymentElement />
      {errorMessage && (
        <div
          style={{ color: "#c62828", marginTop: "1rem", fontSize: "0.9rem" }}
        >
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        disabled={isProcessing || !stripe || !elements}
        className="luxe-btn primary-luxe"
        style={{
          width: "100%",
          marginTop: "1.5rem",
          padding: "1rem",
          backgroundColor: "var(--text-pink)",
          color: "#fff",
          border: "none",
          fontWeight: "bold",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        {isProcessing ? "Processing..." : `Pay £${price}`}
      </button>
    </form>
  );
};

// HELPER: Get London Time details
const getLondonTimeDetails = () => {
  const now = new Date();

  const formatAsYMD = (d) => {
    const options = { timeZone: "Europe/London" };
    const year = new Intl.DateTimeFormat("en-GB", {
      ...options,
      year: "numeric",
    }).format(d);
    const month = new Intl.DateTimeFormat("en-GB", {
      ...options,
      month: "2-digit",
    }).format(d);
    const day = new Intl.DateTimeFormat("en-GB", {
      ...options,
      day: "2-digit",
    }).format(d);
    return `${year}-${month}-${day}`;
  };

  const dateStr = formatAsYMD(now);
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const tomorrowStr = formatAsYMD(tomorrow);
  const dayAfter = new Date(now.getTime() + 48 * 60 * 60 * 1000);
  const dayAfterStr = formatAsYMD(dayAfter);

  const hourStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    hour: "numeric",
    hourCycle: "h23",
  }).format(now);
  const hour = parseInt(hourStr, 10);
  const minStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    minute: "numeric",
  }).format(now);
  const minute = parseInt(minStr, 10);
  const weekdayStr = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    weekday: "short",
  }).format(now);

  return { dateStr, tomorrowStr, dayAfterStr, hour, minute, weekdayStr };
};

// HELPER: Calculates min date
const calculateMinDate = (isSameDayPaused = false, productId) => {
  const { dateStr, tomorrowStr, dayAfterStr, hour } = getLondonTimeDetails();
  const SAME_DAY_IDS = [1, 3, 10, 11];
  const isSameDayEligible = SAME_DAY_IDS.includes(productId);

  if (hour >= 19) {
    if (isSameDayEligible) return tomorrowStr;
    return dayAfterStr;
  }
  if (isSameDayEligible && !isSameDayPaused) {
    return dateStr;
  }
  return tomorrowStr;
};

// HELPER: Generates time slots
const generateTimeSlots = (selectedDate) => {
  if (!selectedDate) return [];
  const dateObj = new Date(selectedDate);
  const isSunday = dateObj.getUTCDay() === 0;
  const closingHour = isSunday ? 18 : 19;
  const startHour = 11;

  const allSlots = [];
  for (let h = startHour; h <= closingHour; h++) {
    allSlots.push(`${h.toString().padStart(2, "0")}:00`);
    if (h < closingHour) {
      allSlots.push(`${h.toString().padStart(2, "0")}:30`);
    }
  }

  const {
    dateStr: todayStr,
    tomorrowStr,
    hour: currentHour,
    minute: currentMinute,
  } = getLondonTimeDetails();

  if (selectedDate === todayStr && currentHour < 9) {
    return allSlots.filter((slot) => {
      const [slotHour, slotMinute] = slot.split(":").map(Number);
      return slotHour * 60 + slotMinute >= 12 * 60 + 30;
    });
  }
  if (selectedDate === tomorrowStr && currentHour >= 17) {
    return allSlots.filter((slot) => {
      const [slotHour, slotMinute] = slot.split(":").map(Number);
      return slotHour * 60 + slotMinute >= 12 * 60 + 30;
    });
  }
  if (selectedDate === todayStr && currentHour >= 9) {
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    const bufferMinutes = 60;
    return allSlots.filter((slot) => {
      const [slotHour, slotMinute] = slot.split(":").map(Number);
      return slotHour * 60 + slotMinute >= currentTotalMinutes + bufferMinutes;
    });
  }
  return allSlots;
};

const ProductDetail = () => {
  const { id } = useParams();

  const initialProductMeta = useMemo(() => {
    return staticProductsData.find((item) => item.id === parseInt(id));
  }, [id]);

  const [product, setProduct] = useState(initialProductMeta);
  const [selectedSize, setSelectedSize] = useState(
    initialProductMeta ? initialProductMeta.sizes[0] : null,
  );

  const isGiftBox = product?.category === "Gift Boxes";
  const isSlice = product?.category === "Slices";

  const availableBases = useMemo(
    () => [
      { flavor_name: "Vanilla", extra_cost: 0 },
      { flavor_name: "Chocolate", extra_cost: 2 },
      { flavor_name: "Red Velvet", extra_cost: 2 },
    ],
    [],
  );

  const [selectedBase, setSelectedBase] = useState(availableBases[0]);
  const [quantity, setQuantity] = useState(1);

  const baseExtraCost =
    !isGiftBox && !isSlice && selectedBase
      ? parseFloat(selectedBase.extra_cost)
      : 0;

  const currentTotalPrice = selectedSize
    ? (parseFloat(selectedSize.price) + baseExtraCost) * quantity
    : 0;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [message, setMessage] = useState("");

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [clientSecret, setClientSecret] = useState("");

  const [minAllowedDate, setMinAllowedDate] = useState(
    calculateMinDate(false, initialProductMeta?.id),
  );

  const [dateError, setDateError] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await fetch("https://www.lovelycakes.shop/api/settings");
        if (res.ok) {
          const data = await res.json();
          setMinAllowedDate(
            calculateMinDate(data.isSameDayPaused, product?.id),
          );
        }
      } catch (err) {
        console.error("Failed to fetch settings", err);
      }
    };
    fetchSettings();
  }, [product?.id]);

  useEffect(() => {
    if (targetDate) {
      const slots = generateTimeSlots(targetDate);
      setAvailableSlots(slots);
      if (timeSlot && !slots.includes(timeSlot)) setTimeSlot("");
    } else {
      setAvailableSlots([]);
      setTimeSlot("");
    }
  }, [targetDate]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden) {
        try {
          const res = await fetch("https://www.lovelycakes.shop/api/settings");
          if (res.ok) {
            const data = await res.json();
            const newMinDate = calculateMinDate(
              data.isSameDayPaused,
              product?.id,
            );
            setMinAllowedDate(newMinDate);

            if (targetDate && targetDate < newMinDate) {
              setTargetDate("");
              setAvailableSlots([]);
              setDateError(
                "Same-day ordering was just disabled due to high volume. Please select a new date.",
              );
            }
          }
        } catch (e) {
          setMinAllowedDate(calculateMinDate(false, product?.id));
        }
        if (targetDate) setAvailableSlots(generateTimeSlots(targetDate));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [targetDate, product?.id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (product?.name) {
      document.title = `Lovely Cakes | ${product.name}`;
      let metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute(
          "content",
          product.desc || product.longDesc.substring(0, 150),
        );
      } else {
        metaDescription = document.createElement("meta");
        metaDescription.name = "description";
        metaDescription.content =
          product.desc || product.longDesc.substring(0, 150);
        document.head.appendChild(metaDescription);
      }
    }
    return () => {
      document.title = "Lovely Cakes";
    };
  }, [product]);

  useEffect(() => {
    const loadProductData = async () => {
      if (!initialProductMeta) return;
      try {
        const encodedName = encodeURIComponent(initialProductMeta.name);
        const response = await fetch(
          `https://www.lovelycakes.shop/api/cake-pricing?name=${encodedName}`,
        );

        if (response.ok) {
          const dbPrices = await response.json();
          if (dbPrices && dbPrices.length > 0) {
            const updatedSizes = dbPrices.map((dbRow) => ({
              size: dbRow.size,
              price: parseFloat(dbRow.price),
            }));
            const lowestPrice = Math.min(...updatedSizes.map((s) => s.price));

            setProduct((prev) => ({
              ...prev,
              price: `From £${lowestPrice}`,
              sizes: updatedSizes,
            }));

            setSelectedSize((prevSize) => {
              const matchingUpdated = updatedSizes.find(
                (s) => s.size === prevSize?.size,
              );
              return matchingUpdated || updatedSizes[0];
            });
          }
        }
      } catch (error) {
        console.error("Database pricing failed. Using fallback data.", error);
      }
    };

    loadProductData();
  }, [initialProductMeta]);

  const isFormComplete =
    name.trim() !== "" &&
    email.trim() !== "" &&
    phone.trim() !== "" &&
    targetDate !== "" &&
    timeSlot !== "" &&
    message.trim() !== "";

  useEffect(() => {
    if (isFormComplete && product && selectedSize) {
      fetch("https://lovelycakes.shop/api/create-payment-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cakeName: product.name,
          size: selectedSize.size,
          quantity: quantity,
          baseFlavor:
            isGiftBox || isSlice
              ? "N/A"
              : selectedBase?.flavor_name || "Vanilla",
          price: currentTotalPrice,
        }),
      })
        .then((res) => res.json())
        .then((data) => setClientSecret(data.clientSecret))
        .catch((err) => console.error("Error fetching intent:", err));
    } else {
      setClientSecret("");
    }
  }, [
    isFormComplete,
    selectedSize?.size,
    quantity,
    selectedBase?.flavor_name,
    currentTotalPrice,
    product?.name,
    isGiftBox,
    isSlice,
  ]);

  if (!product) return null;

  const handlePaymentSuccess = async (stripePaymentId) => {
    const orderData = {
      productName: product.name,
      size: selectedSize.size,
      quantity: quantity,
      baseFlavor:
        isGiftBox || isSlice ? "N/A" : selectedBase?.flavor_name || "Vanilla",
      price: currentTotalPrice,
      customerName: name,
      customerEmail: email,
      customerPhone: phone,
      targetDate: targetDate,
      targetTime: timeSlot,
      message: message,
      sourceId: stripePaymentId,
    };

    try {
      const response = await fetch(
        "https://www.lovelycakes.shop/api/cake-checkout",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orderData),
        },
      );

      if (response.ok) {
        const successData = await response.json();
        setPaymentResult({
          paymentId: successData.paymentId || orderData.sourceId,
        });
        setIsSubmitted(true);
        window.scrollBy({ top: 200, behavior: "smooth" });
      } else {
        const errorData = await response.json();
        alert(`Order Save Error: ${errorData.error}`);
      }
    } catch (error) {
      console.error("Checkout failed:", error);
      alert(
        "Payment successful, but we had trouble saving your order to our dashboard. Please contact us.",
      );
    }
  };

  return (
    <div className="product-detail-wrapper">
      <div className="product-detail-grid">
        <div className="product-detail-visual">
          <div className="back-link-container">
            <Link to="/cakes" className="back-link">
              &larr; Back to Menu
            </Link>
          </div>
          {product.image && (
            <img
              src={product.image}
              alt={product.name}
              className="detail-image"
            />
          )}
        </div>

        <div className="product-detail-info">
          <div className="info-content-inner">
            <div
              className="detail-meta"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <span className="detail-category">{product.category}</span>
              {product.tag && <span className="detail-tag">{product.tag}</span>}

              <span
                className="delivery-badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "4px 10px",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                  fontWeight: "600",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  backgroundColor: [1, 3, 10, 11].includes(product.id)
                    ? "#e8f5e9"
                    : "#e3f2fd",
                  color: [1, 3, 10, 11].includes(product.id)
                    ? "#2e7d32"
                    : "#1565c0",
                  border: `1px solid ${
                    [1, 3, 10, 11].includes(product.id) ? "#c8e6c9" : "#bbdefb"
                  }`,
                }}
              >
                {[1, 3, 10, 11].includes(product.id)
                  ? "⚡ Same Day Click & Collect"
                  : "📅 Collect From Tomorrow"}
              </span>
            </div>

            <h1 className="detail-title">{product.name}</h1>
            <p className="detail-price">£{currentTotalPrice.toFixed(2)}</p>

            {/* --- NEW PILL UI SECTION FOR OPTIONS --- */}
            <div
              className="size-selector-container"
              style={{ marginTop: "1.5rem" }}
            >
              {product.sizes.length > 1 ? (
                <div className="selection-group">
                  <label className="selection-label">
                    {isSlice ? "Select Slice Flavor" : "Available Sizes"}
                  </label>
                  <div className="pill-grid">
                    {product.sizes.map((sizeOption) => (
                      <button
                        key={sizeOption.size}
                        type="button"
                        className={`pill-btn ${selectedSize?.size === sizeOption.size ? "active" : ""}`}
                        onClick={() => setSelectedSize(sizeOption)}
                      >
                        <span className="pill-name">
                          {sizeOption.size.replace(" Slice", "")}
                        </span>
                        <span className="pill-price">
                          £{parseFloat(sizeOption.price).toFixed(2)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    marginBottom: "1.5rem",
                    color: "var(--text-muted)",
                    fontSize: "1.05rem",
                  }}
                >
                  <strong>{isSlice ? "Flavor:" : "Size:"}</strong>{" "}
                  {product.sizes[0].size}
                </div>
              )}

              {!isGiftBox && !isSlice && (
                <div className="selection-group">
                  <label className="selection-label">Sponge Base</label>
                  <div className="pill-grid">
                    {availableBases.map((base) => (
                      <button
                        key={base.flavor_name}
                        type="button"
                        className={`pill-btn ${selectedBase?.flavor_name === base.flavor_name ? "active" : ""}`}
                        onClick={() => setSelectedBase(base)}
                      >
                        <span className="pill-name">{base.flavor_name}</span>
                        <span className="pill-price">
                          {base.extra_cost > 0
                            ? `+£${base.extra_cost.toFixed(2)}`
                            : "Standard"}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="selection-group">
                <label className="selection-label">Quantity</label>
                <div className="quantity-stepper">
                  <button
                    type="button"
                    className="stepper-btn"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  >
                    −
                  </button>
                  <span className="stepper-value">{quantity}</span>
                  <button
                    type="button"
                    className="stepper-btn"
                    onClick={() => setQuantity(quantity + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            {/* --- END NEW PILL UI SECTION --- */}

            <div className="checkout-section">
              <h3
                className="contact-title"
                style={{
                  marginTop: 0,
                  marginBottom: "1.5rem",
                  color: "var(--text-blue)",
                }}
              >
                Order This {isGiftBox ? "Gift Box" : isSlice ? "Slice" : "Cake"}
              </h3>

              {isSubmitted ? (
                <div style={{ textAlign: "center", padding: "1.5rem" }}>
                  <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>
                    ✨
                  </div>
                  <h4
                    style={{
                      margin: "0 0 1rem 0",
                      fontFamily: "var(--font-heading)",
                      fontSize: "1.75rem",
                      color: "var(--text-blue)",
                    }}
                  >
                    Order Confirmed
                  </h4>
                  <p
                    style={{
                      margin: "0 0 1.5rem 0",
                      color: "#555",
                      lineHeight: "1.6",
                    }}
                  >
                    Thank you, {name}. Your payment for the{" "}
                    <strong>{product.name}</strong> was completely successful.
                  </p>
                  {paymentResult && paymentResult.paymentId && (
                    <div
                      style={{
                        margin: "1.5rem auto",
                        padding: "1rem",
                        backgroundColor: "#fff",
                        border: "1px dashed var(--text-blue)",
                        display: "inline-block",
                        borderRadius: "4px",
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          fontSize: "0.9rem",
                          color: "var(--text-blue)",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                        }}
                      >
                        Receipt ID
                      </p>
                      <p
                        style={{
                          margin: "0.5rem 0 0 0",
                          fontFamily: "monospace",
                          fontSize: "1.1rem",
                        }}
                      >
                        {paymentResult.paymentId}
                      </p>
                    </div>
                  )}
                  <p
                    style={{
                      margin: "1.5rem 0 0 0",
                      fontSize: "0.95rem",
                      color: "#555",
                    }}
                  >
                    We have sent a full confirmation receipt to{" "}
                    <strong>{email}</strong>.<br />
                    We look forward to baking for you!
                  </p>
                </div>
              ) : (
                <div className="checkout-form">
                  <div className="input-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      autoComplete="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label htmlFor="date">When do you need this? *</label>
                    {dateError && (
                      <div
                        style={{
                          padding: "0.8rem",
                          backgroundColor: "#fce8ec",
                          color: "var(--text-pink)",
                          border: "1px solid var(--text-pink)",
                          borderRadius: "4px",
                          marginBottom: "0.5rem",
                          fontSize: "0.9rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        ⚠️ {dateError}
                      </div>
                    )}
                    <input
                      type="date"
                      id="date"
                      min={minAllowedDate}
                      value={targetDate}
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        if (selectedDate && selectedDate < minAllowedDate) {
                          setDateError(
                            "Sorry, we need a bit more notice! Please select a valid available date.",
                          );
                          setTargetDate("");
                        } else {
                          setDateError("");
                          setTargetDate(selectedDate);
                        }
                      }}
                      required
                      style={{
                        border: dateError
                          ? "1px solid var(--text-pink)"
                          : "1px solid var(--text-blue)",
                      }}
                    />
                  </div>

                  {/* TIME SLOT: Now using the Pill UI! */}
                  {targetDate && (
                    <div
                      className="selection-group"
                      style={{
                        animation: "fadeIn 0.3s ease",
                        marginTop: "1.25rem",
                      }}
                    >
                      <label
                        className="selection-label"
                        style={{
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          color: "var(--text-blue)",
                          marginBottom: "0.4rem",
                        }}
                      >
                        Collection Time *
                      </label>
                      {availableSlots.length > 0 ? (
                        <div className="pill-grid time-slot-grid">
                          {availableSlots.map((slot) => (
                            <button
                              key={slot}
                              type="button"
                              className={`pill-btn ${timeSlot === slot ? "active" : ""}`}
                              onClick={() => setTimeSlot(slot)}
                            >
                              <span
                                className="pill-name"
                                style={{ margin: 0, fontSize: "1rem" }}
                              >
                                {slot}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div
                          style={{
                            padding: "0.8rem",
                            backgroundColor: "#fce8ec",
                            color: "var(--text-pink)",
                            border: "1px solid var(--text-pink)",
                            borderRadius: "4px",
                            fontSize: "0.9rem",
                          }}
                        >
                          No time slots remaining for this date. Please select
                          another day.
                        </div>
                      )}
                    </div>
                  )}

                  <div className="input-group">
                    <label htmlFor="message">
                      Message / Specific Requests *
                    </label>
                    <textarea
                      id="message"
                      rows="3"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="Any specific design requests or delivery details?"
                      required
                    ></textarea>
                  </div>

                  <div style={{ marginTop: "2rem" }}>
                    {isFormComplete && clientSecret ? (
                      <Elements
                        stripe={stripePromise}
                        options={{ clientSecret }}
                      >
                        <StripeCheckoutForm
                          price={currentTotalPrice.toFixed(2)}
                          onSuccess={handlePaymentSuccess}
                        />
                      </Elements>
                    ) : (
                      <div
                        style={{
                          padding: "1rem",
                          backgroundColor: "#fff",
                          color: "var(--text-blue)",
                          border: "1px solid var(--text-blue)",
                          borderRadius: "4px",
                          textAlign: "center",
                          fontSize: "0.95rem",
                        }}
                      >
                        Please fill in all the details above to unlock the
                        secure payment form.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              className="dietary-badges"
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "3rem",
                flexWrap: "wrap",
                fontWeight: "600",
                color: "var(--text-blue)",
                fontSize: "1rem",
                textTransform: "uppercase",
                letterSpacing: "1.5px",
              }}
            >
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span style={{ fontSize: "1.3rem" }}>🥚</span> 100% Eggless
              </span>
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span style={{ fontSize: "1.3rem" }}>✨</span> Halal
              </span>
              <span
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <span style={{ fontSize: "1.3rem" }}>🌱</span> Vegan
              </span>
            </div>

            <div className="detail-description">
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "1rem",
                  fontSize: "1.2rem",
                  fontWeight: "500",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "var(--text-primary)",
                }}
              >
                About this {isGiftBox ? "Box" : isSlice ? "Slice" : "Cake"}
              </h3>
              <p>{product.longDesc}</p>
              <p>
                Handcrafted using the finest organic flours, free-range eggs,
                and premium Madagascan vanilla. Every item is baked to order
                ensuring absolute perfection for your celebration.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* <WhatsAppFloat /> */}
      <Footer />
    </div>
  );
};

export default ProductDetail;
